import { showAlertNotification } from 'components/common';
import { Dayjs } from 'dayjs';
import { useAppDispatch, useAppSelector } from 'hooks';
import { EInvoice, InvoiceRecord, InvoiceStatus } from 'models/EInvoice';
import moment from 'moment';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useCreateEInvoiceMutation,
  useDownloadEInvoiceMutation,
  useGetAdjustmentEInvoicesQuery,
  useGetAllEInvoicesQuery,
  useGetReplacementEInvoicesQuery,
  useSignEInvoiceMutation,
} from 'services/invoice.service';
import { TableCacheItem } from 'stores/slices/tableCacheSlice';
import { QueryParams } from 'types';
import { EInvoiceFilter, InvoiceListMode } from 'types/invoice.type';

const buildDefaultInvoiceQuery = (tableState?: TableCacheItem<EInvoice>, overrides: Partial<QueryParams> = {}) =>
  ({
    size: tableState?.pageSize ?? 10,
    page: tableState?.page ?? 1,
    sort: tableState?.sortField
      ? `${String(tableState.sortField)}:${tableState.sortOrder === 'descend' ? 'desc' : 'asc'}`
      : 'updatedAt:desc',
    KHMSHDon: '2',
    TNLap: moment().startOf('quarter').format('YYYY-MM-DD'),
    DNLap: moment().endOf('quarter').format('YYYY-MM-DD'),
    ...overrides,
  }) as unknown as QueryParams;

const extractApiErrorMessage = (error: unknown, fallback: string) => {
  const apiError = error as {
    data?: { message?: string | { message?: string[] } };
    message?: string;
  };
  const nestedMessage = apiError?.data?.message;

  if (typeof nestedMessage === 'string' && nestedMessage.trim()) {
    return nestedMessage;
  }

  if (nestedMessage && typeof nestedMessage === 'object' && Array.isArray(nestedMessage.message)) {
    return nestedMessage.message[0] || fallback;
  }

  if (typeof apiError?.message === 'string' && apiError.message.trim()) {
    return apiError.message;
  }

  return fallback;
};

export const useEInvoiceOperation = () => {
  const [openDraftModal, setOpenDraftModal] = useState(false);
  const [createEInvoice] = useCreateEInvoiceMutation();
  const [downloadEInvoice] = useDownloadEInvoiceMutation();
  const [signEInvoice] = useSignEInvoiceMutation();
  const pageKey = 'invoice-list';
  const dispatch = useAppDispatch();
  const tableState = useAppSelector((s) => s.tableCache[pageKey]) as TableCacheItem<EInvoice> | undefined;
  const [listMode, setListMode] = useState<InvoiceListMode>('all');
  const [filteredQuery, setFilteredQuery] = useState<QueryParams>(buildDefaultInvoiceQuery(tableState));
  const allInvoicesQuery = useGetAllEInvoicesQuery(
    {
      query: filteredQuery,
    },
    { skip: listMode !== 'all' },
  );
  const adjustmentInvoicesQuery = useGetAdjustmentEInvoicesQuery(
    {
      query: filteredQuery,
    },
    { skip: listMode !== 'adjustments' },
  );
  const replacementInvoicesQuery = useGetReplacementEInvoicesQuery(
    {
      query: filteredQuery,
    },
    { skip: listMode !== 'replacements' },
  );
  const activeInvoicesQuery =
    listMode === 'adjustments'
      ? adjustmentInvoicesQuery
      : listMode === 'replacements'
        ? replacementInvoicesQuery
        : allInvoicesQuery;
  const { data: eInvoiceData, isLoading, isFetching } = activeInvoicesQuery;
  const [numberFilter, setNumberFilter] = useState<string>('0');
  const navigate = useNavigate();
  const handleSignEInvoice = useCallback(
    async (id: string) => {
      try {
        await signEInvoice(id).unwrap();
        showAlertNotification({
          type: 'success',
          title: 'Kí hoá đơn điện tử',
          message: `Kí hoá đơn điện tử thành công`,
        });
        setFilteredQuery((prev) => ({ ...prev, filter: '' }) as unknown as QueryParams);
        navigate('/invoices/electronic-invoices?newChange=true');
      } catch (error) {
        showAlertNotification({
          type: 'error',
          title: 'Kí hoá đơn điện tử',
          message: extractApiErrorMessage(error, 'Kí hoá đơn điện tử thất bại'),
        });
        console.log('useProductOperations', error);
      }
    },
    [navigate, signEInvoice],
  );
  const handleSubmit = useCallback(
    async (values: Partial<InvoiceRecord>) => {
      try {
        const res = await createEInvoice(values).unwrap();
        const invoiceStatus = res?.data?.invoice?.status;
        const isDraftFallback = Boolean(
          res?.data?.isDraftFallback || res?.data?.isInvoiceCreated || invoiceStatus === InvoiceStatus.DRAFT,
        );

        if (isDraftFallback) {
          setOpenDraftModal(true);
          return;
        }

        if (invoiceStatus === InvoiceStatus.ISSUED) {
          showAlertNotification({
            type: 'success',
            title: 'Tạo hoá đơn điện tử',
            message: 'Bạn đã tạo hoá đơn điện tử thành công',
          });

          setFilteredQuery((prev) => ({ ...prev, filter: '' }) as unknown as QueryParams);
          navigate('/invoices/electronic-invoices?newChange=true');
          return;
        }

        showAlertNotification({
          type: 'warning',
          title: 'Tạo hoá đơn điện tử',
          message: 'Hóa đơn đã được tiếp nhận nhưng trạng thái chưa hoàn tất. Vui lòng kiểm tra lại danh sách hóa đơn.',
        });
        setFilteredQuery((prev) => ({ ...prev, filter: '' }) as unknown as QueryParams);
        navigate('/invoices/electronic-invoices?newChange=true');
      } catch (error) {
        showAlertNotification({
          type: 'error',
          title: 'Tạo hoá đơn điện tử',
          message: extractApiErrorMessage(error, 'Bạn đã tạo hoá đơn điện tử thất bại'),
        });
        console.log('useProductOperations', error);
      }
    },
    [createEInvoice, navigate],
  );
  const handleDraftModalConfirm = () => {
    setOpenDraftModal(false);
    navigate('/invoices/electronic-invoices?newChange=true');
  };
  const handleDownload = useCallback(
    async (id: string) => {
      try {
        const rs = await downloadEInvoice(id).unwrap();
        const filePath = rs?.data?.invoice?.filePath as string;
        if (filePath) {
          window.open(filePath, '_blank');
        }
        showAlertNotification({
          type: 'success',
          title: 'Tạo hoá đơn điện tử',
          message: `Bạn đã tải hoá đơn điện tử thành công`,
        });
      } catch (error) {
        showAlertNotification({
          type: 'error',
          title: 'Tạo hoá đơn điện tử',
          message: `Bạn đã tải hoá đơn điện tử thất bại`,
        });
        console.log('useProductOperations', error);
      }
    },
    [downloadEInvoice],
  );
  const handleListModeChange = useCallback((nextMode: InvoiceListMode) => {
    setListMode(nextMode);
    setFilteredQuery((prev) => ({ ...prev, page: 1 }) as unknown as QueryParams);
  }, []);
  const countActiveFilters = (filter: EInvoiceFilter): number => {
    let count = 0;
    Object.entries(filter).forEach(([key, value]) => {
      if (key === 'listMode') {
        if (value && value !== 'all') {
          count++;
        }
        return;
      }

      if (Array.isArray(value)) {
        // dateRange: [start, end]
        if (value[0] !== null && value[1] !== null) {
          count++;
        }
      } else if (typeof value === 'string') {
        if (value.trim() !== '') {
          count++;
        }
      } else if (typeof value === 'number') {
        if (value !== null && value !== undefined) {
          count++;
        }
      }
    });

    return count;
  };
  const handleApplyFilter = useCallback((newFilter: EInvoiceFilter) => {
    if (!newFilter) return;
    const number = countActiveFilters(newFilter);
    setNumberFilter(number > 0 ? number.toString() : '0');
    const { dateRange, listMode: nextListMode, ...rest } = newFilter;
    let TNLap = moment().startOf('quarter').format('YYYY-MM-DD');
    let DNLap = moment().endOf('quarter').format('YYYY-MM-DD');
    if (dateRange) {
      const [start, end] = (dateRange as unknown as [Dayjs | null, Dayjs | null] | null) || [];

      TNLap = start?.format('YYYY-MM-DD') || TNLap;
      DNLap = end?.format('YYYY-MM-DD') || DNLap;
    }

    if (nextListMode) {
      setListMode(nextListMode);
    }

    setFilteredQuery(
      (prev) =>
        ({
          ...prev,
          ...rest,
          TNLap,
          DNLap,
          page: 1,
        }) as unknown as QueryParams,
    );
  }, []);
  const handleClearFilter = useCallback(
    (KHHDon?: string) => {
      setFilteredQuery(buildDefaultInvoiceQuery(tableState, { KHHDon }));
      setListMode('all');
      setNumberFilter('0');
    },
    [tableState],
  );
  return {
    handleSubmit,
    handleDownload,
    eInvoiceData,
    isLoading,
    isFetching,
    filteredQuery,
    setFilteredQuery,
    dispatch,
    pageKey,
    tableState,
    handleApplyFilter,
    handleClearFilter,
    numberFilter,
    listMode,
    setListMode,
    handleListModeChange,
    handleSignEInvoice,
    handleDraftModalConfirm,
    openDraftModal,
    setOpenDraftModal,
  };
};
